// src/admin/components/reports/charts/RevenueChart.jsx
import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { formatCurrency, formatMonth } from '../../../../models/reportModel';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';

const RevenueChart = ({ data, isDarkMode }) => {
  const { t } = useTranslation();
  
  // Check if RTL language
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // Format data for the chart
  const chartData = data.map(item => ({
    name: formatMonth(item.month),
    rent: parseFloat(item.rent_revenue || 0),
    service: parseFloat(item.service_revenue || 0),
    commission: parseFloat(item.commission_revenue || 0),
    insurance: parseFloat(item.insurance_revenue || 0),
    sale: parseFloat(item.sale_revenue || 0)
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const totalRevenue = payload.reduce((sum, entry) => sum + entry.value, 0);
      
      return (
        <div className={`p-3 rounded-lg shadow-lg ${isDarkMode ? 'bg-zinc-900 text-white' : 'bg-white text-gray-900'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <p className="font-medium">{label}</p>
          <p className="text-sm mt-1">
            <span className="font-medium">{t('total')}:</span> {formatCurrency(totalRevenue)}
          </p>
          <div className="mt-1">
            {payload.map((entry, index) => (
              <div 
                key={`tooltip-${index}`} 
                className="flex items-center text-sm mt-1"
              >
                <div 
                  className={`w-3 h-3 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span>{t(entry.name.toLowerCase())}: {formatCurrency(entry.value)}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
  
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
        barGap={0}
        barCategoryGap="20%"
        layout="horizontal" // Keep horizontal layout for both LTR and RTL
      >
        <CartesianGrid 
          strokeDasharray="3 3" 
          vertical={false} 
          stroke={isDarkMode ? '#374151' : '#e5e7eb'} 
        />
        <XAxis 
          dataKey="name"
          tick={{ fill: isDarkMode ? '#9ca3af' : '#4b5563' }} 
          axisLine={{ stroke: isDarkMode ? '#374151' : '#d1d5db' }}
          reversed={isRTL} // Reverse X-axis for RTL
        />
        <YAxis 
          tickFormatter={(value) => formatCurrency(value)}
          tick={{ fill: isDarkMode ? '#9ca3af' : '#4b5563' }} 
          axisLine={{ stroke: isDarkMode ? '#374151' : '#d1d5db' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ 
            paddingTop: '10px', 
            color: isDarkMode ? '#e5e7eb' : '#1f2937' 
          }} 
        />
        <Bar 
          dataKey="rent" 
          name={t('rent')} 
          stackId="a" 
          fill="#3b82f6" 
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="service" 
          name={t('service')} 
          stackId="a" 
          fill="#10b981" 
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="commission" 
          name={t('commission')} 
          stackId="a" 
          fill="#8b5cf6" 
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="insurance"
          name={t('insurance')}
          stackId="a"
          fill="#f59e0b"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          dataKey="sale"
          name={t('sales')}
          stackId="a"
          fill="#f97316"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default RevenueChart;